import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Check,
    HelpCircle,
    Ruler,
    Layers,
    Waves,
    Truck,
    Route,
} from "lucide-react";
import Seo from "@/components/layout/Seo";
import JsonLd from "@/components/layout/JsonLd";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import Reveal from "@/components/layout/Reveal";
import SectionCTA from "@/components/marketing/SectionCTA";
import InlineCTA from "@/components/marketing/InlineCTA";
import PrestationInternalLinks from "@/components/marketing/PrestationInternalLinks";
import ContactForm from "@/components/marketing/ContactForm";
import { SERVICES } from "@/lib/site";
import { buildBreadcrumb, buildService, buildFaqPage, SITE_ORIGIN } from "@/lib/schema";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const SLUG = "terrassement";

const PRESTATIONS = [
    {
        icon: <Layers className="w-5 h-5" />,
        title: "Préparation de terrain",
        text: "Préparation de terrains pour villas, immeubles, accès, parkings, cours, plateformes, extensions, aménagements extérieurs ou infrastructures. Nous analysons les niveaux, les accès, la configuration du site et les contraintes du projet avant intervention.",
        bullets: [
            "Préparation avant construction",
            "Préparation avant fondations",
            "Plateformes et surfaces extérieures",
            "Accès de chantier",
            "Adaptation des niveaux",
        ],
    },
    {
        icon: <Ruler className="w-5 h-5" />,
        title: "Fouilles, excavation et tranchées",
        text: "Réalisation de fouilles, excavations, tranchées et décaissements selon les besoins du chantier. Ces travaux peuvent concerner des fondations, des réseaux, des canalisations, des accès, des ouvrages extérieurs ou des préparations techniques.",
        bullets: [
            "Fouilles pour fondations",
            "Excavation de terrain",
            "Tranchées techniques",
            "Préparation pour réseaux",
            "Accès et zones de travail",
        ],
    },
    {
        icon: <Waves className="w-5 h-5" />,
        title: "Décaissement, nivellement et remblaiement",
        text: "Travaux de décaissement, nivellement, réglage des surfaces et remblaiement afin d’adapter le terrain à son usage futur : accès véhicules, parking, cour, plateforme, surface carrossable, jardin ou aménagement extérieur.",
        bullets: [
            "Décaissement de surface",
            "Nivellement de terrain",
            "Remblaiement",
            "Gestion des pentes",
            "Préparation avant revêtement",
        ],
    },
    {
        icon: <Truck className="w-5 h-5" />,
        title: "Évacuation des terres et matériaux",
        text: "Les terres, déblais, gravats et matériaux issus du terrassement doivent être triés, transportés et orientés vers des filières adaptées selon leur nature. Nous organisons l’évacuation en fonction des volumes, de l’accès au chantier et des exigences du projet.",
        bullets: [
            "Évacuation des terres",
            "Gestion des déblais",
            "Tri des matériaux selon leur nature",
            "Transport et organisation logistique",
            "Adaptation en cas de matériaux particuliers ou non prévus",
        ],
    },
];

const PROJECT_TYPES = [
    "Terrassement pour villa",
    "Terrassement pour immeuble",
    "Préparation de terrain avant construction",
    "Accès de chantier",
    "Accès véhicules",
    "Cours et parkings",
    "Plateformes extérieures",
    "Préparation avant enrobés",
    "Préparation avant aménagement extérieur",
    "Fouilles pour réseaux ou canalisations",
    "Adaptation de terrain en pente",
    "Évacuation de terres et déblais",
];

const METHODE = [
    {
        title: "Analyse du terrain",
        text: "Nous étudions la localisation du chantier, l’accès, la topographie, la nature visible du terrain, les volumes estimés, les réseaux connus et les contraintes principales.",
        bullets: [
            "Localisation du chantier",
            "Accès et circulation",
            "Volumes estimés",
            "Contraintes techniques",
        ],
    },
    {
        title: "Organisation des travaux",
        text: "Nous préparons l’intervention selon les prestations prévues : fouilles, décaissement, évacuation, remblaiement, nivellement ou préparation de plateforme.",
        bullets: [
            "Planification des étapes",
            "Choix des moyens nécessaires",
            "Coordination du chantier",
            "Sécurité des accès",
        ],
    },
    {
        title: "Exécution du terrassement",
        text: "Les travaux sont réalisés selon les besoins du projet et les conditions constatées sur place, avec une attention particulière portée aux niveaux, aux accès, aux matériaux et à la sécurité.",
        bullets: [
            "Fouilles et excavation",
            "Décaissement",
            "Nivellement",
            "Remblaiement",
        ],
    },
    {
        title: "Évacuation et remise en ordre",
        text: "Les matériaux issus du chantier sont organisés et évacués selon leur nature. La zone d’intervention est remise en ordre selon les prestations prévues.",
        bullets: [
            "Évacuation des terres",
            "Gestion des matériaux",
            "Remise en ordre",
            "Suivi de fin de chantier",
        ],
    },
];

const VIGILANCE = [
    {
        icon: <Route className="w-5 h-5" />,
        title: "Accès au chantier",
        text: "L’accès conditionne l’organisation des travaux, les moyens à prévoir, la circulation, le chargement des matériaux et la sécurité de l’intervention.",
    },
    {
        icon: <Layers className="w-5 h-5" />,
        title: "Nature du terrain",
        text: "La pente, la portance, la présence de roche, d’eau, de remblais ou de réseaux existants peuvent influencer la méthode de terrassement.",
    },
    {
        icon: <Truck className="w-5 h-5" />,
        title: "Gestion des matériaux",
        text: "Les terres, déblais ou gravats doivent être gérés selon leur nature, les volumes concernés et les filières appropriées.",
    },
];

const POURQUOI = [
    {
        title: "Analyse du chantier",
        text: "Chaque demande est étudiée selon la localisation, les accès, la configuration du terrain, les volumes et les contraintes techniques.",
    },
    {
        title: "Sécurité prise en compte",
        text: "L’organisation des travaux tient compte des accès, des personnes, des ouvrages existants, des réseaux connus et de l’environnement immédiat.",
    },
    {
        title: "Travaux structurés",
        text: "Les étapes de terrassement sont organisées afin de faciliter la suite du chantier : fondations, accès, revêtements ou aménagements extérieurs.",
    },
    {
        title: "Gestion des matériaux",
        text: "Les terres, déblais et matériaux issus du chantier sont pris en compte dans l’analyse, l’organisation et l’exécution des travaux.",
    },
    {
        title: "Intervention locale",
        text: "Basés à Plan-les-Ouates, nous intervenons à Genève, dans le canton de Genève et dans toute la Suisse romande.",
    },
    {
        title: "Solutions adaptées",
        text: "Chaque projet reçoit une approche adaptée au terrain, à l’usage prévu, aux contraintes logistiques et aux besoins du client.",
    },
];

const FAQS = [
    {
        question: "Combien de temps dure un chantier de terrassement pour une villa ?",
        answer: "La durée dépend de la surface, des volumes à déplacer, de l’accès au chantier, de la nature du sol, des évacuations à prévoir et des contraintes du projet. Un petit terrassement peut être réalisé rapidement, tandis qu’un chantier plus complexe nécessite une analyse plus détaillée et une organisation adaptée.",
    },
    {
        question: "Comment se déroule l’évacuation des terres à Genève ?",
        answer: "Les terres et matériaux issus du terrassement sont évalués selon leur nature, leur volume et les contraintes du chantier. Ils doivent être triés, transportés et orientés vers des filières adaptées. Certains matériaux particuliers ou non prévus peuvent nécessiter une analyse ou une adaptation de l’organisation.",
    },
    {
        question: "Faut-il une autorisation pour un terrassement en propriété privée ?",
        answer: "Cela dépend du type de travaux, de leur ampleur, de la commune, du site concerné et des éventuelles modifications du terrain. Certains projets peuvent nécessiter une autorisation ou des démarches particulières, notamment en cas de construction, transformation, occupation du domaine public ou intervention proche d’ouvrages existants.",
    },
    {
        question: "Intervenez-vous sur des terrains difficiles d’accès ou en pente ?",
        answer: "Oui, chaque demande est étudiée selon l’accès, la pente, la configuration du terrain et les moyens nécessaires. Les terrains complexes demandent une analyse plus précise afin de définir une méthode d’intervention adaptée.",
    },
    {
        question: "Le devis est-il gratuit et engageant ?",
        answer: "La demande de devis est gratuite. Le devis est établi selon les informations disponibles et les éléments visibles ou communiqués. Il devient engageant après acceptation, sous réserve des conditions prévues dans l’offre et des éventuels éléments imprévus découverts pendant le chantier.",
    },
    {
        question: "Travaillez-vous pour les particuliers comme pour les professionnels ?",
        answer: "Oui. RCPS Terrassement intervient pour les particuliers, propriétaires, architectes, régies, promoteurs, entreprises générales, collectivités et professionnels à Genève et en Suisse romande.",
    },
];

export default function TerrassementPage() {
    const otherServices = SERVICES.filter((s) => s.slug !== SLUG);

    return (
        <>
            <Seo
                title="Terrassement Genève | Fouilles, excavation et préparation de terrain"
                description="RCPS Terrassement Sàrl réalise vos travaux de terrassement à Genève et en Suisse romande : fouilles, excavation, décaissement, nivellement, remblaiement, préparation de terrain et évacuation des terres."
            />
            <JsonLd
                data={buildBreadcrumb([
                    { name: "Accueil", path: "/" },
                    { name: "Prestations", path: "/prestations" },
                    { name: "Terrassement", path: "/prestations/terrassement" },
                ])}
                id="breadcrumb-terrassement"
            />
            <JsonLd
                data={buildService({
                    slug: "terrassement",
                    name: "Terrassement à Genève et en Suisse romande",
                    serviceType: "Terrassement, fouilles et excavation",
                    description:
                        "Travaux de terrassement pour particuliers et professionnels : fouilles, excavation, décaissement, nivellement, remblaiement, préparation de terrain et évacuation des terres à Genève et en Suisse romande.",
                    image: `${SITE_ORIGIN}/rcps-assets/prestation-terrassement-01.jpeg`,
                })}
                id="service-terrassement"
            />
            <JsonLd
                data={buildFaqPage(
                    `${SITE_ORIGIN}/prestations/terrassement`,
                    FAQS
                )}
                id="faq-terrassement"
            />

            {/* Hero */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="terrassement-hero"
            >
                <img
                    src="https://customer-assets.emergentagent.com/job_elated-beaver-5/artifacts/6coeh1e9_Terrassement%20-%20Grande.jpeg"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-navy-900/55 via-navy-800/45 to-navy-700/70" />
                <div className="absolute inset-0 bg-topo-dark opacity-40 mix-blend-overlay" />
                <div className="absolute inset-0 noise-overlay" />
                <div className="relative rcps-container pt-24 md:pt-28 pb-16 md:pb-24 grid md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <Reveal>
                            <Breadcrumbs
                                variant="onImage"
                                trail={[
                                    { name: "Accueil", path: "/" },
                                    { name: "Prestations", path: "/prestations" },
                                    { name: "Terrassement", path: "/prestations/terrassement" },
                                ]}
                            />
                        </Reveal>
                        <Reveal delay={0.05}>
                            <p className="kicker text-white/80 mt-5">
                                <span className="hairline bg-white/60" /> Prestation · Terrassement
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1
                                className="mt-5 font-heading text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight"
                                data-testid="terrassement-h1"
                            >
                                Travaux de terrassement à Genève et en Suisse romande
                            </h1>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 text-white/85 max-w-2xl text-base md:text-lg leading-relaxed">
                                RCPS Terrassement Sàrl réalise des travaux de terrassement pour les
                                particuliers, architectes, régies, promoteurs, entreprises générales,
                                collectivités et professionnels. Basés à Plan-les-Ouates, nous
                                intervenons à Genève, dans l’ensemble du canton de Genève et dans toute
                                la Suisse romande.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-4 text-white/75 max-w-2xl text-base md:text-lg leading-relaxed">
                                Nous préparons les terrains pour les constructions, accès, parkings,
                                aménagements extérieurs, fondations, plateformes, réseaux et
                                infrastructures, avec une analyse adaptée à la localisation du chantier,
                                aux accès, à la nature du sol, aux volumes à déplacer et aux contraintes
                                techniques.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-4 flex md:justify-end">
                        <Reveal delay={0.25}>
                            <Link
                                to="/contact"
                                data-testid="hero-cta-devis"
                                className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white text-navy-700 font-semibold hover:bg-concrete-50 transition"
                            >
                                Demander un devis <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Introduction */}
            <section className="bg-white" data-testid="terrassement-intro">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
                    <div className="md:col-span-6">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Terrassement à Genève
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-700 tracking-tight leading-[1.1]">
                                Préparer votre terrain avec méthode.
                            </h2>
                        </Reveal>
                        <div className="mt-8 space-y-5 text-navy-700/85 text-base md:text-lg leading-[1.8]">
                            <Reveal delay={0.08}>
                                <p>
                                    Le terrassement est une étape essentielle avant de construire,
                                    transformer ou aménager un terrain. Il permet de préparer le sol,
                                    d’ajuster les niveaux, de créer les accès, de réaliser les fouilles
                                    nécessaires et d’organiser l’évacuation des terres ou matériaux issus
                                    du chantier.
                                </p>
                            </Reveal>
                            <Reveal delay={0.12}>
                                <p>
                                    À Genève comme en Suisse romande, un terrassement doit tenir compte de
                                    nombreux paramètres : nature du terrain, pente, accès aux machines,
                                    présence éventuelle de réseaux, voisinage, volumes à évacuer,
                                    contraintes de sécurité, autorisations éventuelles et conditions
                                    météorologiques. RCPS Terrassement analyse ces éléments afin de
                                    proposer une intervention claire, réaliste et adaptée au projet.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <Reveal delay={0.1}>
                            <div className="rounded-md overflow-hidden border border-concrete-200 aspect-[4/3]">
                                <img
                                    src="/rcps-assets/prestation-terrassement-01.jpeg"
                                    alt="Chantier de terrassement RCPS — préparation de terrain"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Prestations de terrassement */}
            <section
                className="bg-concrete-50 border-y border-concrete-200"
                data-testid="terrassement-prestations"
            >
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Nos travaux de terrassement
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-navy-700 tracking-tight">
                                    Des prestations adaptées à chaque terrain.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        {PRESTATIONS.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.05}>
                                <article
                                    className="h-full bg-white rounded-md border border-concrete-200 p-7 md:p-8"
                                    data-testid={`prestation-card-${i + 1}`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-11 h-11 rounded-md bg-concrete-100 text-navy-700 flex items-center justify-center flex-shrink-0">
                                            {p.icon}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-mono-num text-xs text-steel">
                                                {String(i + 1).padStart(2, "0")}
                                            </p>
                                            <h3 className="mt-1 font-heading text-xl md:text-2xl font-semibold text-navy-700 tracking-tight">
                                                {p.title}
                                            </h3>
                                        </div>
                                    </div>
                                    <p className="mt-5 text-navy-700/80 leading-relaxed">{p.text}</p>
                                    <ul className="mt-5 grid sm:grid-cols-2 gap-2">
                                        {p.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-2 text-navy-700 text-sm"
                                            >
                                                <span className="mt-1.5 w-3.5 h-3.5 rounded-full bg-concrete-100 flex items-center justify-center flex-shrink-0">
                                                    <Check className="w-2.5 h-2.5 text-navy-700" strokeWidth={2.5} />
                                                </span>
                                                <span className="leading-relaxed">{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Types de projets */}
            <section className="bg-white" data-testid="terrassement-project-types">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Pour quels projets ?
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-700 tracking-tight leading-[1.1]">
                                Terrassement pour particuliers, professionnels et collectivités.
                            </h2>
                            <p className="mt-6 text-navy-700/80 leading-relaxed">
                                RCPS Terrassement intervient sur des projets de tailles et de
                                configurations variées, à Genève et en Suisse romande. Chaque demande
                                est étudiée selon le type de terrain, la localisation, les accès
                                disponibles, les volumes à déplacer, les travaux prévus et les
                                contraintes techniques.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <div className="mb-5 aspect-[16/10] rounded-md overflow-hidden border border-concrete-200">
                                <img src="/rcps-assets/prestation-terrassement-02.jpeg" alt="Chantier de terrassement RCPS" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <ul className="grid sm:grid-cols-2 gap-2.5">
                                {PROJECT_TYPES.map((t) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-3 bg-concrete-50 border border-concrete-200 rounded-md px-4 py-3 text-navy-700 text-sm md:text-base leading-snug"
                                    >
                                        <span className="mt-1 w-4 h-4 rounded-full bg-white border border-concrete-200 flex items-center justify-center flex-shrink-0">
                                            <Check className="w-2.5 h-2.5 text-navy-700" strokeWidth={2.5} />
                                        </span>
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Méthode */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="terrassement-methode"
            >
                <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
                <div className="absolute inset-0 noise-overlay pointer-events-none" />
                <div className="relative rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-white/70">
                                    <span className="hairline bg-white/60" /> Notre méthode
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold tracking-tight">
                                    Une intervention préparée avant chaque chantier.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {METHODE.map((m, i) => (
                            <Reveal key={m.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-white/12 bg-white/[0.03] p-6"
                                    data-testid={`methode-step-${i + 1}`}
                                >
                                    <p className="font-mono-num text-white/60 text-xs">
                                        Étape {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-3 font-heading text-lg md:text-xl font-semibold">
                                        {m.title}
                                    </h3>
                                    <p className="mt-3 text-white/75 text-sm leading-relaxed">
                                        {m.text}
                                    </p>
                                    <ul className="mt-4 space-y-1.5">
                                        {m.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-2 text-white/75 text-sm"
                                            >
                                                <span className="mt-1.5 w-1 h-1 rounded-full bg-white/60 flex-shrink-0" />
                                                <span className="leading-snug">{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Points de vigilance */}
            <InlineCTA
                variant="navy"
                title="Un projet de terrassement à Genève ou en Suisse romande ?"
                text="Décrivez-nous votre chantier : accès, contraintes et objectifs. Nous revenons vers vous avec une analyse claire."
                primary={{ label: "Demander un devis", to: "/contact" }}
                secondary={{ label: "À propos de nous", to: "/entreprise" }}
                dataTestId="terrassement-inline-cta"
            />
            <section className="bg-white" data-testid="terrassement-vigilance">
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-12">
                        <div className="md:col-span-5">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> À prendre en compte
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight leading-[1.15]">
                                    Terrassement à Genève : accès, autorisations et contraintes de chantier.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-7 space-y-5 text-navy-700/80 leading-[1.8]">
                            <Reveal delay={0.08}>
                                <p>
                                    À Genève et dans les communes de Suisse romande, certains travaux
                                    peuvent nécessiter des démarches spécifiques, notamment en cas
                                    d’occupation du domaine public, de modification importante du
                                    terrain, de travaux proches d’ouvrages existants, d’intervention
                                    sur accès, de présence de réseaux ou de volumes importants à
                                    évacuer.
                                </p>
                            </Reveal>
                            <Reveal delay={0.12}>
                                <p>
                                    RCPS Terrassement étudie chaque demande en tenant compte de ces
                                    éléments afin d’anticiper les contraintes techniques, logistiques
                                    et administratives. Les autorisations éventuelles dépendent du
                                    type de projet, de la commune, du site concerné et des travaux
                                    prévus.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <Reveal delay={0.16}>
                        <div className="mb-10 md:mb-12 aspect-[21/9] rounded-md overflow-hidden border border-concrete-200">
                            <img
                                src="/rcps-assets/prestation-terrassement-03.jpeg"
                                alt="Chantier de terrassement RCPS — accès, contraintes et sécurité"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-5">
                        {VIGILANCE.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-concrete-200 bg-concrete-50 p-6 md:p-7"
                                    data-testid={`vigilance-card-${i + 1}`}
                                >
                                    <div className="w-11 h-11 rounded-md bg-white text-navy-700 border border-concrete-200 flex items-center justify-center">
                                        {v.icon}
                                    </div>
                                    <p className="mt-5 font-mono-num text-xs text-steel">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-1 font-heading text-lg md:text-xl font-semibold text-navy-700 tracking-tight">
                                        {v.title}
                                    </h3>
                                    <p className="mt-2 text-navy-700/75 text-sm md:text-base leading-relaxed">
                                        {v.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pourquoi RCPS */}
            <section
                className="relative bg-navy-700 text-white overflow-hidden"
                data-testid="terrassement-pourquoi"
            >
                <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
                <div className="absolute inset-0 noise-overlay pointer-events-none" />
                <div className="relative rcps-container py-20 md:py-24">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-white/70">
                                    <span className="hairline bg-white/60" /> Pourquoi RCPS ?
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold tracking-tight">
                                    Un terrassement organisé, clair et adapté au terrain.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <Reveal delay={0.1}>
                        <div className="mb-10 aspect-[21/9] rounded-md overflow-hidden border border-white/12">
                            <img
                                src="/rcps-assets/prestation-terrassement-04.jpeg"
                                alt="Chantier de terrassement RCPS — organisation et exécution"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {POURQUOI.map((h, i) => (
                            <Reveal key={h.title} delay={i * 0.05}>
                                <div
                                    className="h-full rounded-md border border-white/12 bg-white/[0.03] p-6"
                                    data-testid={`pourquoi-card-${i + 1}`}
                                >
                                    <p className="font-mono-num text-white/60 text-xs">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-3 font-heading text-lg font-semibold">
                                        {h.title}
                                    </h3>
                                    <p className="mt-2 text-white/75 text-sm leading-relaxed">
                                        {h.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact / Formulaire */}
            <section
                className="bg-concrete-50 border-y border-concrete-200"
                data-testid="terrassement-contact"
            >
                <div className="rcps-container py-20 md:py-24 grid md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Contact
                            </p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 leading-tight tracking-tight">
                                Parlons de votre projet de terrassement.
                            </h2>
                            <p className="mt-5 text-navy-700/80 leading-relaxed">
                                Vous avez un terrain à préparer, une fouille à réaliser, un accès à
                                créer, une surface à décaisser ou des terres à évacuer ? Transmettez-nous
                                les informations principales de votre chantier. RCPS Terrassement vous
                                répondra après analyse de votre demande.
                            </p>
                            <ul className="mt-6 space-y-2 text-navy-700/80 text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Analyse selon la localisation et les accès</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Prise en compte des volumes et contraintes</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                    <span>Réponse après étude de votre demande</span>
                                </li>
                            </ul>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.08}>
                            <ContactForm defaultProjectType="Terrassement" />
                            <p className="mt-4 text-xs md:text-sm text-navy-700/60 leading-relaxed">
                                Réponse après analyse de votre demande · devis personnalisé · sans
                                engagement.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                className="bg-white border-t border-concrete-200"
                data-testid="terrassement-faq"
            >
                <div className="rcps-container py-20 md:py-24">
                    <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
                        <div className="md:col-span-5 md:sticky md:top-28">
                            <Reveal>
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> Questions fréquentes
                                </p>
                                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight leading-tight">
                                    Vos questions sur les travaux de terrassement.
                                </h2>
                                <p className="mt-5 text-navy-700/80 leading-relaxed">
                                    Vous avez une question sur un projet de terrassement à Genève ou en
                                    Suisse romande ? Contactez-nous pour nous présenter votre chantier,
                                    ses accès, sa localisation et les travaux souhaités.
                                </p>
                                <div className="mt-6 flex items-center gap-3 text-navy-700">
                                    <span className="w-10 h-10 rounded-md bg-concrete-100 flex items-center justify-center">
                                        <HelpCircle className="w-5 h-5" />
                                    </span>
                                    <Link
                                        to="/contact"
                                        data-testid="faq-contact-link"
                                        className="text-sm font-semibold link-underline"
                                    >
                                        Poser une question à RCPS
                                    </Link>
                                </div>
                            </Reveal>
                        </div>
                        <div className="md:col-span-7">
                            <Reveal delay={0.08}>
                                <Accordion
                                    type="single"
                                    collapsible
                                    className="w-full space-y-3"
                                    data-testid="terrassement-faq-accordion"
                                >
                                    {FAQS.map((f, i) => (
                                        <AccordionItem
                                            key={f.question}
                                            value={`item-${i}`}
                                            className="border border-concrete-200 rounded-md bg-white overflow-hidden [&[data-state=open]]:border-navy-700 transition"
                                        >
                                            <AccordionTrigger
                                                className="px-5 py-4 md:px-6 md:py-5 text-left hover:no-underline group"
                                                data-testid={`faq-question-${i}`}
                                            >
                                                <div className="flex items-start gap-4 pr-2">
                                                    <span className="font-mono-num text-xs text-navy-700/50 mt-1 flex-shrink-0">
                                                        {String(i + 1).padStart(2, "0")}
                                                    </span>
                                                    <span className="font-heading text-navy-700 font-semibold text-base md:text-lg leading-snug">
                                                        {f.question}
                                                    </span>
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-5 md:px-6 pb-5 md:pb-6">
                                                <div className="pl-9 text-navy-700/80 text-sm md:text-base leading-[1.75]">
                                                    {f.answer}
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <PrestationInternalLinks
                currentSlug={SLUG}
                relatedCategories={[
                    "Terrassement",
                    "Génie civil, voirie & enrobés",
                    "Aménagements extérieurs & enrochement",
                ]}
                bridgeText={
                    <>
                        <p>
                            Le terrassement est presque toujours la première étape d’un chantier. Selon la
                            suite envisagée, les travaux se prolongent naturellement vers d’autres corps
                            de métier que RCPS Terrassement maîtrise également.
                        </p>
                        <p>
                            Si votre projet inclut la création d’un accès, d’une plateforme carrossable
                            ou d’un parking après le décaissement, découvrez également nos prestations de{" "}
                            <Link
                                to="/prestations/genie-civil-voirie-enrobes"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                génie civil, voirie et enrobés à Genève
                            </Link>
                            .
                        </p>
                        <p>
                            Pour un chantier de rénovation ou de reconstruction sur un site existant,
                            nous réalisons également les{" "}
                            <Link
                                to="/prestations/demolition-deconstruction"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                travaux de démolition et de déconstruction à Genève et en Suisse romande
                            </Link>
                            . Enfin, pour finaliser un aménagement de terrain — talus, soutènements ou{" "}
                            <Link
                                to="/prestations/amenagements-exterieurs-enrochement"
                                className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                            >
                                aménagements extérieurs et enrochement à Genève
                            </Link>
                            {" "}— nous accompagnons vos projets jusqu’au rendu final.
                        </p>
                    </>
                }
            />

            {/* CTA final */}
            <SectionCTA
                title="Un projet de terrassement à concrétiser ?"
                text="Présentez-nous votre chantier. Nous étudions votre demande de terrassement à Genève ou en Suisse romande selon la localisation, les accès, les volumes, la nature du terrain et les contraintes techniques."
            />
        </>
    );
}
